import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';

/*
 * One metered account's AI credits, for a user manager on /users
 * (routes/ai_credits.py): what it has this month, the purchased balance's
 * moves, credits added (a negative number takes some back, never below zero)
 * and its own monthly allowance in place of its role's. Each change needs a
 * reason and is audited.
 */
export default function ManageAiCredits({ account, onClose }) {
  const { t } = useTranslation('credits');
  const [data, setData] = useState(null);
  // { text: the server's words, or null; key: this dialog's own line }
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [allowance, setAllowance] = useState('');
  const [allowanceReason, setAllowanceReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');

  const load = useCallback(() => Promise.resolve()
    .then(() => api.get('/ai-credits', { params: { user_id: account.user_id } }))
    .then(res => {
      setData(res.data);
      setAllowance(res.data?.allowance_override ?? '');
    })
    .catch(err => setError({ text: errorText(err), key: 'manage.loadFailed' })), [account.user_id]);
  useEffect(() => { load(); }, [load]);

  const save = async (request, done) => {
    setBusy(true);
    setError(null);
    setSaved('');
    try {
      await request();
      done();
      await load();
    } catch (err) {
      setError({ text: errorText(err), key: 'manage.saveFailed' });
    } finally {
      setBusy(false);
    }
  };

  const grant = (e) => {
    e.preventDefault();
    save(() => api.post(`/ai-credits/${account.user_id}/grant`, { amount: Number(amount), reason }),
      () => { setAmount(''); setReason(''); setSaved(t('manage.granted')); });
  };
  const setOwnAllowance = (e) => {
    e.preventDefault();
    save(() => api.put(`/ai-credits/${account.user_id}/allowance`, {
      monthly_free_allowance: allowance === '' ? null : Number(allowance), reason: allowanceReason,
    }), () => { setAllowanceReason(''); setSaved(t('manage.allowanceSaved')); });
  };

  const amountOk = /^-?\d+$/.test(String(amount).trim()) && Number(amount) !== 0;
  return (
    <Dialog title={t('manage.title', { name: account.username })} onClose={onClose} busy={busy}
      dirty={!!(amount || reason || allowanceReason)} testId="manage-ai-credits" width={520}>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 13 }}>{error.text || t(error.key)}</p>}
      {saved && <p role="status" style={{ color: 'var(--c-success-3-fg)', fontSize: 13 }}>{saved}</p>}
      {!data ? (
        !error && <p style={muted}>{t('dialog.loading')}</p>
      ) : (
        <>
          <p style={{ fontSize: 14, margin: '0 0 10px' }} data-testid="manage-ai-credits-summary">
            {t('manage.summary', {
              used: data.free_used, allowance: data.monthly_free_allowance, purchased: data.purchased_token_balance,
            })}
          </p>

          <form onSubmit={grant} style={formStyle}>
            <h3 style={heading}>{t('manage.grantTitle')}</h3>
            <label style={label} htmlFor="ai-credit-amount">{t('manage.amount')}</label>
            <input id="ai-credit-amount" type="number" step="1" value={amount} onChange={e => setAmount(e.target.value)}
              style={input} data-testid="manage-ai-credits-amount" title={t('tip.amount')} />
            <label style={label} htmlFor="ai-credit-reason">{t('manage.reason')}</label>
            <input id="ai-credit-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
              style={input} data-testid="manage-ai-credits-reason" title={t('tip.reason')} />
            <Button type="submit" variant="primary" busy={busy} disabled={!amountOk || reason.trim().length < 3}
              data-testid="manage-ai-credits-grant">{t('manage.grant')}</Button>
          </form>

          <form onSubmit={setOwnAllowance} style={formStyle}>
            <h3 style={heading}>{t('manage.allowanceTitle')}</h3>
            <label style={label} htmlFor="ai-credit-allowance">{t('manage.allowance')}</label>
            <input id="ai-credit-allowance" type="number" min="0" step="1" value={allowance}
              onChange={e => setAllowance(e.target.value)} style={input} data-testid="manage-ai-credits-allowance"
              title={t('tip.allowance')} />
            <p style={muted}>{t('manage.allowanceHelp')}</p>
            <label style={label} htmlFor="ai-credit-allowance-reason">{t('manage.reason')}</label>
            <input id="ai-credit-allowance-reason" value={allowanceReason} maxLength={500}
              onChange={e => setAllowanceReason(e.target.value)} style={input} title={t('tip.reason')}
              data-testid="manage-ai-credits-allowance-reason" />
            <Button type="submit" busy={busy} disabled={allowanceReason.trim().length < 3}
              data-testid="manage-ai-credits-save-allowance">{t('manage.saveAllowance')}</Button>
          </form>

          <h3 style={heading}>{t('dialog.history')}</h3>
          {(data.entries || []).length === 0 ? (
            <p style={muted}>{t('dialog.noHistory')}</p>
          ) : (
            <ul style={list}>
              {data.entries.map(e => (
                <li key={e.entry_id} style={row}>
                  <span>{t(`kind.${e.kind}`)}{e.reason ? ` · ${e.reason}` : ''}{e.by ? ` · ${e.by}` : ''}</span>
                  <span dir="ltr" style={{ fontWeight: 600 }}>{e.amount > 0 ? `+${e.amount}` : e.amount}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <ActionBar>
        <Button onClick={onClose} disabled={busy}>{t('dialog.close')}</Button>
      </ActionBar>
    </Dialog>
  );
}

const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '4px 0' };
const heading = { fontSize: 14, margin: '12px 0 6px' };
const label = { display: 'block', fontSize: 13, fontWeight: 600, margin: '6px 0 2px' };
const input = { display: 'block', width: '100%', padding: 8, border: '1px solid var(--c-border-strong)', borderRadius: 6,
  boxSizing: 'border-box', marginBottom: 6 };
const formStyle = { borderTop: '1px solid var(--c-border)', paddingTop: 6, marginTop: 6 };
const list = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6, fontSize: 13 };
const row = { display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 };
